import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';

import { AxisTick } from './AxisTick';
import { ChartTooltip } from './ChartTooltip';
import { CHROME } from './palette';

/** ~6px per character at 11px, so this many characters fit the axis width. */
const AXIS_WIDTH = 250;
const MAX_LABEL = 38;

export interface RankedBarRow {
  label: string;
  value: number;
  detail: { key: string; label: string; value: string }[];
}

/**
 * Ranked magnitude with long category names, so horizontal bars. One measure and
 * one series, which is why there is a single hue and no legend — the title names
 * what is being measured.
 */
export function RankedBars({
  rows,
  color,
  unit,
  height,
}: {
  rows: RankedBarRow[];
  color: string;
  unit: string;
  height?: number;
}) {
  if (rows.length === 0) {
    return null;
  }

  return (
    <ResponsiveContainer width="100%" height={height ?? Math.max(180, rows.length * 30 + 40)}>
      <BarChart data={rows} layout="vertical" margin={{ top: 4, right: 32, bottom: 4, left: 4 }} barCategoryGap={4}>
        <CartesianGrid stroke={CHROME.grid} horizontal={false} />
        <XAxis
          type="number"
          tick={{ fill: CHROME.inkMuted, fontSize: 11 }}
          axisLine={false}
          tickLine={false}
          tickFormatter={(value: number) => `${value}${unit}`}
        />
        <YAxis
          type="category"
          dataKey="label"
          axisLine={false}
          tickLine={false}
          width={AXIS_WIDTH}
          /* Product names run long; wrapping them collides with the next tick,
             so each label is trimmed to one line and named fully in the tooltip. */
          tick={(props) => <AxisTick {...props} max={MAX_LABEL} />}
        />
        <Tooltip
          cursor={{ fill: 'rgba(255,255,255,0.05)' }}
          content={({ active, payload }) => {
            if (!active || !payload?.length) {
              return null;
            }

            const row = payload[0].payload as RankedBarRow;

            return <ChartTooltip title={row.label} rows={row.detail} />;
          }}
        />
        <Bar isAnimationActive={false} dataKey="value" fill={color} radius={4} maxBarSize={20} />
      </BarChart>
    </ResponsiveContainer>
  );
}
